import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import GoogleSignInButton from "@/components/GoogleSignInButton";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name}`);
      nav(u.is_admin ? "/app/admin" : "/app/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-neo-bg text-white grid grid-cols-1 md:grid-cols-2">
      <div className="hidden md:flex flex-col justify-between p-12 border-r border-neo-border relative noise">
        <Link to="/" data-testid="auth-brand-link" className="font-mono uppercase tracking-[0.28em] text-sm">
          Neo.Invest
        </Link>
        <div>
          <h1 className="text-5xl font-black tracking-tighter uppercase leading-none">
            Back to<br />the terminal.
          </h1>
          <p className="mt-6 text-neutral-400 max-w-md">
            One ledger. Crypto, equities, treasuries. Log in to continue.
          </p>
        </div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
          // secured with jwt · 7-day sessions
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-neutral-500">
            // sign_in
          </div>
          <h2 className="mt-3 text-4xl font-black tracking-tighter uppercase">Sign in</h2>
          <div className="mt-8 space-y-4">
            <FieldLabel label="Email">
              <input
                data-testid="login-email-input"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-transparent border border-neo-border px-4 py-3 font-mono text-sm focus:outline-none focus:border-white transition-colors"
              />
            </FieldLabel>
            <FieldLabel label="Password">
              <input
                data-testid="login-password-input"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-transparent border border-neo-border px-4 py-3 font-mono text-sm focus:outline-none focus:border-white transition-colors"
              />
            </FieldLabel>
          </div>
          <button
            data-testid="login-submit-button"
            type="submit"
            disabled={busy}
            className="mt-8 w-full bg-white text-black font-mono uppercase tracking-widest text-xs py-4 border border-white hover-invert disabled:opacity-60"
          >
            {busy ? "Signing in…" : "Enter terminal"}
          </button>

          <div className="mt-4 flex items-center gap-3 text-neutral-600 font-mono text-[10px] uppercase tracking-[0.3em]">
            <span className="h-px flex-1 bg-neo-border" /> or <span className="h-px flex-1 bg-neo-border" />
          </div>
          <div className="mt-4">
            <GoogleSignInButton />
          </div>

          <div className="mt-6 text-xs text-neutral-500 font-mono">
            No account yet?{" "}
            <Link data-testid="login-to-register-link" to="/register" className="text-white underline underline-offset-4">
              Open one — $100 bonus
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

function FieldLabel({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
        {label}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
